/**
 * Currency configuration and formatting utilities for Admin
 *
 * Matches backend CurrencyMetadata and CurrencyExtensions
 * Backend returns amounts in major units (decimal), so we only need to format them correctly.
 */

/**
 * Currency metadata matching backend CurrencyMetadata.cs
 */
export const CURRENCY_METADATA = {
  USD: { decimals: 2, minorUnit: "cents", symbol: "$", name: "US Dollar" },
  VND: { decimals: 0, minorUnit: "đồng", symbol: "₫", name: "Vietnamese Dong" },
  JPY: { decimals: 0, minorUnit: "yen", symbol: "¥", name: "Japanese Yen" },
  EUR: { decimals: 2, minorUnit: "cents", symbol: "€", name: "Euro" },
  KWD: { decimals: 3, minorUnit: "fils", symbol: "KD", name: "Kuwaiti Dinar" },
  GBP: { decimals: 2, minorUnit: "pence", symbol: "£", name: "British Pound" },
  INR: { decimals: 2, minorUnit: "paisa", symbol: "₹", name: "Indian Rupee" },
  AUD: {
    decimals: 2,
    minorUnit: "cents",
    symbol: "A$",
    name: "Australian Dollar",
  },
  CAD: {
    decimals: 2,
    minorUnit: "cents",
    symbol: "C$",
    name: "Canadian Dollar",
  },
} as const;

export type CurrencyCode = keyof typeof CURRENCY_METADATA;

/**
 * Get decimal places for a currency
 * @param currency Currency code
 * @returns Number of decimal places (0 for VND/JPY, 2 for USD/EUR/GBP, 3 for KWD)
 */
export function getDecimalPlaces(currency: string): number {
  const code = currency.toUpperCase() as CurrencyCode;
  return CURRENCY_METADATA[code]?.decimals ?? 2; // Default to 2
}

/**
 * Get currency symbol
 * @param currency Currency code
 * @returns Currency symbol
 */
export function getCurrencySymbol(currency: string): string {
  const code = currency.toUpperCase() as CurrencyCode;
  return CURRENCY_METADATA[code]?.symbol ?? currency;
}

/**
 * Get currency info
 * @param currency Currency code
 * @returns Currency metadata or null
 */
export function getCurrencyInfo(currency: string) {
  const code = currency.toUpperCase() as CurrencyCode;
  return CURRENCY_METADATA[code] ?? null;
}

/**
 * Format currency amount with proper decimal places and symbol position
 * Matches backend CurrencyExtensions.FormatAmountFromMajorUnits
 *
 * @param amount Amount in major currency units (backend returns this format)
 * @param currency Currency code (default: VND)
 * @returns Formatted currency string
 *
 * @example
 * formatCurrency(10000, 'VND') // "₫10,000"
 * formatCurrency(1.5, 'USD') // "$1.50"
 * formatCurrency(1.0, 'KWD') // "KD 1.000"
 */
export function formatCurrency(
  amount: number,
  currency: string = "VND",
): string {
  const code = currency.toUpperCase() as CurrencyCode;
  const metadata = CURRENCY_METADATA[code];

  if (!metadata) {
    // Fallback for unknown currencies
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  const { decimals, symbol } = metadata;

  // Format number with proper decimal places
  const formattedNumber = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);

  // Match backend formatting style
  switch (code) {
    case "USD":
      return `$${formattedNumber}`;
    case "VND":
      return `${formattedNumber}₫`;
    case "EUR":
      return `€${formattedNumber}`;
    case "GBP":
      return `£${formattedNumber}`;
    case "JPY":
      return `¥${formattedNumber}`;
    case "KWD":
      return `KD ${formattedNumber}`;
    case "INR":
      return `₹${formattedNumber}`;
    case "AUD":
      return `A$${formattedNumber}`;
    case "CAD":
      return `C$${formattedNumber}`;
    default:
      return `${symbol}${formattedNumber}`;
  }
}

/**
 * Format currency amount with compact notation (e.g., 1.5K, 1.2M)
 * @param amount Amount in major currency units
 * @param currency Currency code
 * @returns Formatted currency string with compact notation
 */
export function formatCurrencyCompact(
  amount: number,
  currency: string = "VND",
): string {
  const code = currency.toUpperCase() as CurrencyCode;
  const metadata = CURRENCY_METADATA[code];

  if (!metadata) {
    return formatCurrency(amount, currency);
  }

  const { decimals, symbol } = metadata;

  const formattedNumber = new Intl.NumberFormat("en-US", {
    notation: "compact",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);

  // Match backend formatting style
  switch (code) {
    case "USD":
      return `$${formattedNumber}`;
    case "VND":
      return `${formattedNumber}₫`;
    case "EUR":
      return `€${formattedNumber}`;
    case "GBP":
      return `£${formattedNumber}`;
    case "JPY":
      return `¥${formattedNumber}`;
    case "KWD":
      return `KD ${formattedNumber}`;
    case "INR":
      return `₹${formattedNumber}`;
    case "AUD":
      return `A$${formattedNumber}`;
    case "CAD":
      return `C$${formattedNumber}`;
    default:
      return `${symbol}${formattedNumber}`;
  }
}

/**
 * Check if currency is supported
 * @param currency Currency code
 * @returns True if supported
 */
export function isSupportedCurrency(currency: string): boolean {
  const code = currency.toUpperCase() as CurrencyCode;
  return code in CURRENCY_METADATA;
}

/**
 * Convert smallest currency unit to display amount.
 * Matches backend CurrencyExtensions.ConvertMinorUnitsToAmount.
 *
 * @example fromMinorUnits(1050, 'USD') => 10.50
 * @example fromMinorUnits(10000, 'VND') => 10000
 * @example fromMinorUnits(1000, 'KWD') => 1.000
 */
export function fromMinorUnits(
  amount: number,
  currency: string = "VND",
): number {
  const decimals = getDecimalPlaces(currency);
  return amount / Math.pow(10, decimals);
}

/**
 * Convert display amount to smallest currency unit.
 * Matches backend CurrencyExtensions.AmountToCents.
 *
 * @example toMinorUnits(10.50, 'USD') => 1050
 * @example toMinorUnits(10000, 'VND') => 10000
 * @example toMinorUnits(1.000, 'KWD') => 1000
 */
export function toMinorUnits(amount: number, currency: string = "VND"): number {
  const decimals = getDecimalPlaces(currency);
  return Math.round(amount * Math.pow(10, decimals));
}
